import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import DeadlineCalendarApi from '@services/deadlineCalendarApi'
import { calendarReviewError, validCalendarReviewVersion } from '@services/calendarReviewRequest'
import { useNotification } from '@context/NotificationContext'
import { CALENDAR_PAGE_SIZE, calendarSyncErrorLabel, createLatestCalendarRequest, parseCalendarYear } from '../calendarRequestState'
import './DeadlineEngineCalendarPanel.css'

const CURRENT_YEAR = new Date().getFullYear()
const VIEWS = [
  ['sources', 'Fontes'],
  ['events', 'Eventos e revisão'],
  ['runs', 'Sincronizações'],
]

function rowsFrom(response) {
  if (Array.isArray(response?.items)) return response.items
  if (Array.isArray(response?.data)) return response.data
  return []
}

function formatDate(value, withTime = false) {
  if (!value) return '-'
  const text = String(value)
  if (!withTime && /^\d{4}-\d{2}-\d{2}$/.test(text)) {
    const [year, month, day] = text.split('-')
    return `${day}/${month}/${year}`
  }
  const parsed = new Date(text)
  if (Number.isNaN(parsed.getTime())) return text.slice(0, 10)
  return parsed.toLocaleString('pt-BR', withTime
    ? { dateStyle: 'short', timeStyle: 'short' }
    : { dateStyle: 'short' })
}

function statusClass(status) {
  return String(status || 'UNKNOWN').toLowerCase().replaceAll('_', '-')
}

function statusLabel(status) {
  const labels = {
    APPROVED: 'Aprovado',
    PENDING_REVIEW: 'Pendente',
    REJECTED: 'Rejeitado',
    SUPERSEDED: 'Substituído',
    SUCCEEDED: 'Concluído',
    FALLBACK: 'Estadual e nacional',
    PARTIAL: 'Parcial',
    FAILED: 'Falhou',
    NOT_MODIFIED: 'Sem alteração',
    RUNNING: 'Executando',
  }
  return labels[status] || status || '-'
}

function sourceRoleLabel(role) {
  const labels = {
    PRIMARY_CALENDAR: 'Calendário principal',
    LOCAL_HOLIDAYS: 'Feriados locais',
    DEADLINE_SUSPENSIONS: 'Suspensões de prazo',
    COURT_CALENDAR: 'Calendário do tribunal',
    AMENDMENTS_WATCHER: 'Atualizações e comunicados',
  }
  return labels[role] || role || 'Calendário judicial'
}

function sourceScopeLabel(source) {
  if (source?.applicableInstances?.length) {
    const labels = { FIRST: '1ª instância', SECOND: '2ª instância', SUPERIOR: 'Tribunal superior' }
    return source.applicableInstances.map((instance) => labels[instance] || instance).join(' · ')
  }
  if (source?.supportsLocalities) return 'Por município ou comarca'
  if (source?.scope === 'NATIONAL') return 'Abrangência nacional'
  if (source?.scope === 'STATE') return 'Abrangência estadual'
  return 'Abrangência do tribunal'
}

function sourceDisplayName(source) {
  return String(source?.key || '')
    .replace(/^tjsp\./i, 'TJSP · ')
    .replace(/[._-]/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function displayEventDescription(description) {
  const text = String(description || '').trim()
  const [first, second] = text.split(' — ', 2)
  return second && first.localeCompare(second, 'pt-BR', { sensitivity: 'base' }) === 0 ? first : text
}

function eventCoverageLabel(event) {
  if (event.city) return event.city
  if (event.comarca) return event.comarca
  return event.scopeType === 'MUNICIPAL' ? 'Município não informado' : 'Abrangência geral'
}

function Stat({ label, value, emphasis }) {
  return (
    <div className={`court-calendar-stat ${emphasis ? 'court-calendar-stat-emphasis' : ''}`}>
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  )
}

function PageControls({ page, rows, loading, onChange, label }) {
  return (
    <nav className="court-calendar-filters" aria-label={`Paginação de ${label}`}>
      <button type="button" className="btn btn-sm btn-outline" disabled={loading || page <= 1} onClick={() => onChange(page - 1)}>Anterior</button>
      <span className="court-calendar-filter-result">Página {page} · {rows} {label} nesta página</span>
      <button type="button" className="btn btn-sm btn-outline" disabled={loading || rows < CALENDAR_PAGE_SIZE} onClick={() => onChange(page + 1)}>Próxima</button>
    </nav>
  )
}

export default function DeadlineEngineCalendarPanel() {
  const { showError, showSuccess, showWarning } = useNotification()
  const [view, setView] = useState('sources')
  const [sources, setSources] = useState([])
  const [events, setEvents] = useState([])
  const [runs, setRuns] = useState([])
  const [loadingByView, setLoadingByView] = useState({ sources: false, events: false, runs: false })
  const [errors, setErrors] = useState({ sources: '', events: '', runs: '' })
  const [eventPage, setEventPage] = useState(1)
  const [runPage, setRunPage] = useState(1)
  const [syncingSource, setSyncingSource] = useState(null)
  const [reviewingEvent, setReviewingEvent] = useState(null)
  const [sourceSearch, setSourceSearch] = useState('')
  const [court, setCourt] = useState('')
  const [status, setStatus] = useState('PENDING_REVIEW')
  const [sourceKey, setSourceKey] = useState('')
  const [eventLocation, setEventLocation] = useState('')
  const [year, setYear] = useState(String(CURRENT_YEAR))
  const yearNumber = parseCalendarYear(year)
  const loading = loadingByView[view]
  const requests = useRef(null)
  if (!requests.current) requests.current = Object.fromEntries(['sources', 'events', 'runs', 'sync', 'review']
    .map((kind) => [kind, createLatestCalendarRequest()]))

  const loadList = useCallback(async (kind, fetchRows, setRows, fallbackError) => {
    const request = requests.current[kind].begin()
    setLoadingByView((previous) => ({ ...previous, [kind]: true }))
    setErrors((previous) => ({ ...previous, [kind]: '' }))
    try {
      const response = await fetchRows(request.signal)
      if (!request.isCurrent()) return
      if (!response.success) {
        const message = response.error || fallbackError
        setErrors((previous) => ({ ...previous, [kind]: message }))
        showError(message)
        return
      }
      setRows(rowsFrom(response))
    } catch {
      if (request.isCurrent()) {
        setErrors((previous) => ({ ...previous, [kind]: fallbackError }))
        showError(fallbackError)
      }
    } finally {
      if (request.isCurrent()) setLoadingByView((previous) => ({ ...previous, [kind]: false }))
    }
  }, [showError])

  const loadSources = useCallback(() => loadList('sources', (signal) => DeadlineCalendarApi.listSources({ signal }),
    setSources, 'Não foi possível carregar as fontes de calendário.'), [loadList])

  const loadEvents = useCallback(() => {
    if (yearNumber === null) {
      requests.current.events.cancel()
      setEvents([])
      setErrors((previous) => ({ ...previous, events: '' }))
      setLoadingByView((previous) => ({ ...previous, events: false }))
      return Promise.resolve()
    }
    return loadList('events', (signal) => DeadlineCalendarApi.listEvents({ court, status, year: yearNumber,
      page: eventPage, pageSize: CALENDAR_PAGE_SIZE }, { signal }), setEvents,
    'Não foi possível carregar os eventos importados.')
  }, [court, eventPage, loadList, status, yearNumber])

  const loadRuns = useCallback(() => loadList('runs', (signal) => DeadlineCalendarApi.listRuns({ court, sourceKey,
    page: runPage, pageSize: CALENDAR_PAGE_SIZE }, { signal }), setRuns,
  'Não foi possível carregar o histórico de sincronizações.'), [court, loadList, runPage, sourceKey])
  const latestEventsReload = useRef(loadEvents)
  latestEventsReload.current = loadEvents
  const activeView = useRef(view)
  activeView.current = view

  const refresh = useCallback(async () => {
    if (view === 'sources') await loadSources()
    if (view === 'events') await loadEvents()
    if (view === 'runs') await loadRuns()
  }, [loadEvents, loadRuns, loadSources, view])

  useEffect(() => {
    const tracker = requests.current.sources
    loadSources()
    return () => tracker.cancel()
  }, [loadSources])

  useEffect(() => {
    if (view !== 'events') return undefined
    const tracker = requests.current.events
    loadEvents()
    return () => tracker.cancel()
  }, [loadEvents, view])

  useEffect(() => {
    if (view !== 'runs') return undefined
    const tracker = requests.current.runs
    loadRuns()
    return () => tracker.cancel()
  }, [loadRuns, view])

  useEffect(() => {
    const trackers = requests.current
    return () => {
      trackers.sync.cancel()
      trackers.review.cancel()
    }
  }, [])

  const filteredSources = useMemo(() => {
    const search = sourceSearch.trim().toLocaleLowerCase('pt-BR')
    return sources.filter((item) => {
      if (court && item.name !== court) return false
      if (!search) return true
      const source = item.source || {}
      return [item.name, source.key, source.role, source.parser, ...(item.states || [])]
        .filter(Boolean)
        .some((value) => String(value).toLocaleLowerCase('pt-BR').includes(search))
    })
  }, [court, sourceSearch, sources])

  const courtOptions = useMemo(() => Array.from(new Set(
    sources.map((item) => item.name).filter(Boolean),
  )).sort((left, right) => left.localeCompare(right, 'pt-BR', { numeric: true })), [sources])

  const pendingEvents = events.filter((item) => item.status === 'PENDING_REVIEW').length
  const failedRuns = runs.filter((item) => item.status === 'FAILED').length
  const filteredEvents = useMemo(() => {
    const search = eventLocation.trim().toLocaleLowerCase('pt-BR')
    if (!search) return events
    return events.filter((event) => [event.city, event.comarca, event.description, event.sourceKey]
      .filter(Boolean)
      .some((value) => String(value).toLocaleLowerCase('pt-BR').includes(search)))
  }, [eventLocation, events])

  const hasEventFilters = Boolean(court || status || eventLocation)

  const sync = async (item) => {
    const source = item.source || {}
    if (!source.key || yearNumber === null) return
    const request = requests.current.sync.begin()
    setSyncingSource(source.key)
    const response = await DeadlineCalendarApi.syncSource(source.key, yearNumber, { signal: request.signal })
    if (!request.isCurrent()) return
    setSyncingSource(null)
    if (!response.success) {
      showError(response.error || `Não foi possível sincronizar ${source.key}.`)
      return
    }
    const summary = response.data || {}
    if (summary.status === 'FAILED') showError(`${source.key}: ${calendarSyncErrorLabel(summary.error)}`)
    else if (summary.status === 'PARTIAL') showWarning(`${source.key}: sincronização parcial. ${calendarSyncErrorLabel(summary.error)}`)
    else showSuccess(summary.status === 'FALLBACK'
      ? `${source.key}: sem eventos próprios para ${yearNumber}. Serão usados os calendários estadual e nacional disponíveis.`
      : summary.status === 'NOT_MODIFIED' ? `${source.key}: fonte sem alterações para ${yearNumber}.`
        : `${source.key}: ${summary.eventsFound ?? 0} evento(s), ${summary.eventsPendingReview ?? 0} pendente(s).`)
    await loadSources()
  }

  const review = async (event, approved) => {
    if (!validCalendarReviewVersion(event.reviewVersion)) {
      showError('Atualize a lista de eventos para obter a versão atual antes de revisar.')
      return
    }
    const promptText = approved
      ? 'Observação da aprovação (opcional):'
      : 'Informe o motivo da rejeição:'
    const reason = window.prompt(promptText, '')
    if (reason === null || (!approved && !reason.trim())) return

    const request = requests.current.review.begin()
    setReviewingEvent(event.id)
    const response = await DeadlineCalendarApi.reviewEvent(event.id, approved, reason.trim() || null, event.reviewVersion, { signal: request.signal })
    if (!request.isCurrent()) return
    setReviewingEvent(null)
    if (!response.success) {
      showError(calendarReviewError(response))
      if (response.status === 409 && activeView.current === 'events') await latestEventsReload.current()
      return
    }
    const approvedMessage = event.suspendsDeadlines
      ? 'Evento aprovado e projetado para o calendário de prazos.'
      : 'Evento aprovado sem efeito no cálculo de prazos.'
    showSuccess(approved ? approvedMessage : 'Evento rejeitado.')
    if (activeView.current === 'events') await latestEventsReload.current()
  }

  const changeCourt = (value) => {
    if (value === court) return
    requests.current.events.cancel()
    requests.current.runs.cancel()
    setEvents([])
    setRuns([])
    setEventPage(1)
    setRunPage(1)
    setCourt(value)
  }

  const changeStatus = (value) => {
    if (value === status) return
    requests.current.events.cancel()
    setEvents([])
    setEventPage(1)
    setStatus(value)
  }

  const changeEventPage = (page) => {
    requests.current.events.cancel()
    setEvents([])
    setEventPage(page)
  }

  const changeRunPage = (page) => {
    requests.current.runs.cancel()
    setRuns([])
    setRunPage(page)
  }

  const changeView = (value) => {
    if (value === view) return
    requests.current.events.cancel()
    requests.current.runs.cancel()
    activeView.current = value
    setView(value)
  }

  return (
    <div className="court-calendar-panel">
      <div className="court-calendar-overview">
        <Stat label="fontes na seleção" value={filteredSources.length} />
        <Stat label="fontes locais" value={filteredSources.filter((item) => item.source?.supportsLocalities).length} />
        <Stat label="eventos pendentes na página" value={pendingEvents} emphasis={pendingEvents > 0} />
        <Stat label="falhas no histórico carregado" value={failedRuns} emphasis={failedRuns > 0} />
      </div>

      <div className="court-calendar-toolbar">
        <div className="court-calendar-view-tabs" role="tablist" aria-label="Gestão de calendários judiciais">
          {VIEWS.map(([id, label]) => (
            <button key={id} type="button" className={view === id ? 'active' : ''} onClick={() => changeView(id)}>
              {label}
            </button>
          ))}
        </div>
        <label className="rules-field court-calendar-court-filter">
          <span className="rules-field-label">Tribunal</span>
          <select value={court} onChange={(event) => changeCourt(event.target.value)}>
            <option value="">Todos os tribunais</option>
            {courtOptions.map((courtCode) => <option value={courtCode} key={courtCode}>{courtCode}</option>)}
          </select>
        </label>
        <label className="rules-field court-calendar-year">
          <span className="rules-field-label">Ano</span>
          <input type="number" min="2000" max="2100" value={year} aria-invalid={yearNumber === null} onChange={(event) => {
            requests.current.events.cancel()
            setEvents([])
            setEventPage(1)
            setYear(event.target.value)
          }} />
        </label>
        <button type="button" className="btn btn-outline" onClick={refresh} disabled={loading || view === 'events' && yearNumber === null}>
          <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
          {loading ? 'Carregando...' : 'Atualizar'}
        </button>
      </div>

      {yearNumber === null ? <p className="deadline-settings-state" role="alert">Informe um ano inteiro entre 2000 e 2100 para consultar eventos ou sincronizar fontes.</p> : null}
      {errors[view] ? <p className="deadline-settings-state" role="alert">{errors[view]}</p> : null}

      {view === 'sources' ? (
        <>
          <div className="court-calendar-filters">
            <label className="rules-field">
              <span className="rules-field-label">Buscar fonte ou tribunal</span>
              <input value={sourceSearch} onChange={(event) => setSourceSearch(event.target.value)} placeholder="TJSP, TST, feriados locais..." />
            </label>
            <span className="court-calendar-filter-result">{filteredSources.length} de {sources.length} fonte(s)</span>
            {sourceSearch ? <button type="button" className="btn btn-sm btn-outline" onClick={() => setSourceSearch('')}>Limpar busca</button> : null}
          </div>
          <div className="deadline-settings-table-wrapper">
            <table className="users-table deadline-settings-table court-calendar-table">
              <thead><tr><th>Tribunal</th><th>Calendário</th><th>Cobertura</th><th>Importação</th><th>Revisão</th><th>Ações</th></tr></thead>
              <tbody>
                {filteredSources.map((item) => {
                  const source = item.source || {}
                  return (
                    <tr key={source.key}>
                      <td><strong>{item.name}</strong><small>Onda {item.implementationWave} · {(item.states || []).join(', ')}</small></td>
                      <td><strong>{sourceDisplayName(source)}</strong><small>{sourceRoleLabel(source.role)}</small></td>
                      <td><span>{sourceScopeLabel(source)}</span><small>{source.supportsLocalities ? 'Localidade identificada no evento' : 'Sem filtro municipal'}</small></td>
                      <td><span>{source.format || 'Documento oficial'}</span><small className="court-calendar-technical">{source.parser}</small></td>
                      <td>{source.requiresHumanReview ? 'Obrigatória' : 'Automática quando inequívoco'}</td>
                      <td>
                        <button type="button" className="btn btn-sm btn-primary" onClick={() => sync(item)} disabled={Boolean(syncingSource) || yearNumber === null || loading}>
                          <i className={`fas ${syncingSource === source.key ? 'fa-spinner fa-spin' : 'fa-cloud-download-alt'}`} />
                          {syncingSource === source.key ? 'Sincronizando...' : yearNumber === null ? 'Sincronizar' : `Sincronizar ${yearNumber}`}
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {!filteredSources.length && !loading && !errors.sources ? <div className="deadline-settings-state">Nenhuma fonte encontrada para os filtros informados.</div> : null}
        </>
      ) : null}

      {view === 'events' ? (
        <>
          <div className="court-calendar-filters court-calendar-event-filters">
            <label className="rules-field"><span className="rules-field-label">Localidade</span><input value={eventLocation} onChange={(event) => setEventLocation(event.target.value)} placeholder="Município ou comarca" /></label>
            <label className="rules-field"><span className="rules-field-label">Status</span><select value={status} onChange={(event) => changeStatus(event.target.value)}><option value="">Todos</option><option value="PENDING_REVIEW">Pendentes</option><option value="APPROVED">Aprovados</option><option value="REJECTED">Rejeitados</option><option value="SUPERSEDED">Substituídos</option></select></label>
            <button type="button" className="btn btn-outline" onClick={loadEvents} disabled={loading || yearNumber === null}>Atualizar</button>
            {hasEventFilters ? <button type="button" className="btn btn-sm btn-outline" onClick={() => { changeCourt(''); setEventLocation(''); changeStatus('') }}>Limpar filtros</button> : null}
          </div>
          <p className="court-calendar-filter-hint">Tribunal, status e ano consultam a base; localidade refina somente os eventos desta página. Navegue pelas páginas para consultar outros dias.</p>
          <div className="deadline-settings-table-wrapper">
            <table className="users-table deadline-settings-table court-calendar-table">
              <thead><tr><th>Data</th><th>Tribunal / fonte</th><th>Evento</th><th>Efeito</th><th>Status</th><th>Ações</th></tr></thead>
              <tbody>
                {filteredEvents.map((event) => (
                  <tr key={event.id}>
                    <td><strong>{formatDate(event.startDate)}</strong>{event.endDate !== event.startDate ? <small>até {formatDate(event.endDate)}</small> : null}</td>
                    <td><strong>{event.courtCode}</strong><small>{eventCoverageLabel(event)} · {event.sourceKey}</small></td>
                    <td><strong>{displayEventDescription(event.description)}</strong><small>{event.sourceKey}</small>{event.reviewReason ? <em>{event.reviewReason}</em> : null}</td>
                    <td>{event.suspendsDeadlines ? <span className="court-calendar-effect yes">Suspende prazos</span> : <span className="court-calendar-effect no">Não projetado</span>}</td>
                    <td><span className={`court-calendar-status ${statusClass(event.status)}`}>{statusLabel(event.status)}</span></td>
                    <td>
                      {event.status === 'PENDING_REVIEW' ? (
                        <div className="deadline-settings-row-actions">
                          <button type="button" className="btn btn-sm btn-primary" disabled={Boolean(reviewingEvent) || loading || !validCalendarReviewVersion(event.reviewVersion)} onClick={() => review(event, true)}><i className="fas fa-check" /> Aprovar</button>
                          <button type="button" className="btn btn-sm btn-danger" disabled={Boolean(reviewingEvent) || loading || !validCalendarReviewVersion(event.reviewVersion)} onClick={() => review(event, false)}><i className="fas fa-ban" /> Rejeitar</button>
                          {!validCalendarReviewVersion(event.reviewVersion) && <small>Atualize a lista antes de revisar.</small>}
                        </div>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!filteredEvents.length && !loading && !errors.events && yearNumber !== null ? <div className="deadline-settings-state">Nenhum evento encontrado nesta página para os filtros informados.</div> : null}
          </div>
          <PageControls page={eventPage} rows={events.length} loading={loading || yearNumber === null} onChange={changeEventPage} label="eventos" />
        </>
      ) : null}

      {view === 'runs' ? (
        <>
          <div className="court-calendar-filters">
            <label className="rules-field"><span className="rules-field-label">Chave da fonte</span><input value={sourceKey} onChange={(event) => {
              requests.current.runs.cancel()
              setRuns([])
              setRunPage(1)
              setSourceKey(event.target.value)
            }} placeholder="tst.calendar" /></label>
            <button type="button" className="btn btn-outline" onClick={loadRuns} disabled={loading}>Aplicar filtro</button>
            {sourceKey ? <button type="button" className="btn btn-sm btn-outline" onClick={() => {
              requests.current.runs.cancel()
              setRuns([])
              setRunPage(1)
              setSourceKey('')
            }}>Limpar filtro</button> : null}
          </div>
          <div className="deadline-settings-table-wrapper">
            <table className="users-table deadline-settings-table court-calendar-table">
              <thead><tr><th>Início</th><th>Fonte</th><th>Ano</th><th>Resultado</th><th>Eventos</th><th>Detalhes</th></tr></thead>
              <tbody>
                {runs.map((run) => (
                  <tr key={run.id}>
                    <td><strong>{formatDate(run.startedAt, true)}</strong><small>{run.finishedAt ? `Fim: ${formatDate(run.finishedAt, true)}` : 'Em andamento'}</small></td>
                    <td><strong>{run.sourceKey}</strong><small>{run.courtCode}</small></td>
                    <td>{run.requestedYear || '-'}</td>
                    <td><span className={`court-calendar-status ${statusClass(run.status)}`}>{statusLabel(run.status)}</span></td>
                    <td><span>{run.eventsFound} encontrados</span><small>{run.eventsCreated} novos · {run.eventsUpdated} atualizados · {run.eventsPendingReview} pendentes</small></td>
                    <td>{run.status === 'FALLBACK'
                      ? 'Sem eventos próprios da 2ª instância neste ano. Usando os calendários estadual e nacional disponíveis.'
                      : calendarSyncErrorLabel(run.error)}{run.error ? <small>{run.error}</small> : null}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!runs.length && !loading && !errors.runs ? <div className="deadline-settings-state">Nenhuma sincronização encontrada nesta página.</div> : null}
          </div>
          <PageControls page={runPage} rows={runs.length} loading={loading} onChange={changeRunPage} label="sincronizações" />
        </>
      ) : null}
    </div>
  )
}
